
import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import 'emoji-mart-vue-fast/css/emoji-mart.css'
import data from 'emoji-mart-vue-fast/data/all.json'
import At from 'vue-at'
import { ModalSize } from '@/components/modal-size'
import Modal from '@/components/Modal.vue'
import Button from '@/components/WebView/Button.vue'
import { pendoTrackEvent } from '@/utils/pendo'

declare let Quill
@Component<ChatInput>({
    components: { Picker, At, Modal, Button },
})
export default class ChatInput extends Vue {
    // combine mention and hashtags
    public get members() {
        return [
            ...this.mentions,
            ...this.tags.map((tag) => ({ name: tag, email: '' })),
        ]
            .filter((member) => member.name !== null) // filter out users without a name
            .sort((a, b) => a.name.localeCompare(b.name))
    }

    public showEmojiPicker = false
    public showSendosoTool = false
    public showHyperlinkModal = false
    public hyperlink = ''
    public quill
    public contentLength = 0
    public emojiIndex = new EmojiIndex(data)

    @Prop({ type: Array, default: () => [] }) public mentions!: Array<{ name }> // can be ChatMention or IRecipient
    @Prop({ type: Array, default: () => [] }) public tags!: string[]
    @Prop({ type: String, default: '' }) public content?: string
    @Prop({ type: String, default: '' }) public placeholder?: string
    @Prop({ type: Boolean, default: false }) public isSendosoEnabled?: boolean
    @Prop({ type: Number, default: 0 }) public maxTextLength?: number
    @Prop({ type: Boolean, default: false }) public htmlMode?: boolean
    @Prop({ type: Boolean, default: false }) public disableEmoji?: boolean
    @Prop({ type: Boolean, default: false }) public disableAutoFocus?: boolean
    @Prop(Function) public onUpdate

    // Sometimes we need to overwrite the style of input; in that condition, you can apply a `customClass` on
    // ChatInput in your page then define the style in your file. Refer to `ChatResponseEmailReplyScreen.vue`
    // for more detail
    @Prop({ type: String, default: '' }) public customClass?: string
    @Prop({ type: String, default: '' }) public toolsCustomClass?: string

    public get modalSize() {
        return ModalSize
    }

    public link(link: string) {
        this.quill.format('link', link)
    }

    public mounted() {
        this.quill = new Quill('#quill-container', {
            placeholder: this.placeholder,
            modules: {
                keyboard: {
                    bindings: {
                        enter: {
                            key: 13, // enter
                            shortKey: true, // cmd on mac, ctrl on windows
                            handler: () => {
                                this.$emit('save')
                            },
                        },
                    },
                },
            },
        })

        this.quill.on('text-change', (delta, oldDelta, source) => {
            const content = this.htmlMode
                ? this.quill.root.innerHTML
                : this.quill.getText()
            this.contentLength = this.quill.getText().length
            this.onUpdate(content, this.contentLength)
        })
        this.updateContent(this.content)
        this.contentLength = this.quill.getText().length
        // move the cursor to the end
        if (!this.disableAutoFocus) {
            this.quill.focus()
            this.quill.setSelection(this.contentLength)
        }
    }

    public filterMatch(name, chunk, at) {
        return (
            name !== null &&
            name.toLowerCase().indexOf(chunk.toLowerCase()) === 0 &&
            // is mention someone and name is in mentions list
            ((at === '@' &&
                this.mentions.filter((mention) => mention.name === name)
                    .length > 0) ||
                // or is tag and name is in tags list
                (at === '#' &&
                    this.tags.filter((tag) => tag === name).length > 0))
        )
    }

    public onInputKeydown(evt) {
        if (evt.key === '@') {
            pendoTrackEvent('mention_started')
        }
    }

    // clear content after saving
    @Watch('content')
    public onContentChange() {
        if (!this.content) {
            this.updateContent(this.content)
        }
        this.contentLength = this.quill.getText().length
    }

    @Watch('placeholder')
    public onPlaceholderChanged() {
        this.quill.root.dataset.placeholder = this.placeholder
    }

    public updateContent(html) {
        if (this.htmlMode) {
            const delta = this.quill.clipboard.convert(html)
            this.quill.setContents(delta, 'silent')
        } else {
            this.quill.setText(html)
        }
        this.contentLength = this.quill.getText().length
    }

    public onClickEmojiPicker() {
        this.showSendosoTool = false
        this.showEmojiPicker = !this.showEmojiPicker
    }

    public onClickSendoso() {
        this.showEmojiPicker = false
        this.showSendosoTool = !this.showSendosoTool
    }

    public onSelectEmoji(emoji) {
        this.showEmojiPicker = false
        this.quill.insertText(this.quill.getSelection(true).index, emoji.native)
    }

    public toggerHyperlinkModal() {
        this.showHyperlinkModal = !this.showHyperlinkModal
    }

    public onOkHyperlink() {
        this.showHyperlinkModal = false
        let link = this.hyperlink.trim()
        if (!link.startsWith('http://') && !link.startsWith('https://')) {
            link = 'http://' + link
        }
        this.quill.format('link', link)
        this.hyperlink = ''
    }

    public onMentionAdded(tag) {
        if (tag.name) {
            pendoTrackEvent('mention_added')
            this.$emit('mentionAdded', tag.name)
        }
    }

    public deleteMatch(name, chunk, suffix) {
        let isMatch = chunk === name + suffix

        if (isMatch) {
            this.$emit('mentionDeleted', name)
        }

        return isMatch
    }
}
